import React, { useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  Dialog,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  BoxSize,
  FulfillmentLineExceptionType,
  PackOrder,
  PackSession,
} from '../../../services/fulfillment/types';
import { EXCEPTION_TYPES } from '../../../services/fulfillment/constants';
import { matchScan } from '../../../services/fulfillment/packApi';
import { BOX_SIZES } from '../../../services/fulfillment/packStubs';
import ScanInput from './ScanInput';
import PackLineItem from './PackLineItem';

interface Props {
  order: PackOrder;
  session: PackSession;
  onSessionChange: (s: PackSession) => void;
  onNext: () => void;
}

let boxCounter = 10;
const nextBoxId = () => `box-${++boxCounter}`;

const suggestFirstBox = (order: PackOrder): BoxSize => {
  const types = order.lines.map(l => l.itemType);
  if (types.some(t => t === 'bike')) return 'Bike Box - Medium';
  if (types.some(t => t === 'frame')) return 'Frame Box';
  if (types.some(t => t === 'wheel')) return 'Wheel Box - Double';
  return 'Small P&A Box';
};

const MockPackScanStep: React.FC<Props> = ({ order, session, onSessionChange, onNext }) => {
  const [activeBoxIdx, setActiveBoxIdx] = useState(0);
  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanWarning, setScanWarning] = useState<string | null>(null);
  const [focusTrigger, setFocusTrigger] = useState(0);
  const [newBoxSize, setNewBoxSize] = useState<BoxSize>(suggestFirstBox(order));
  const [exceptionGid, setExceptionGid] = useState<string | null>(null);
  const [selectedExcType, setSelectedExcType] = useState<FulfillmentLineExceptionType | null>(null);
  const [excNotes, setExcNotes] = useState('');

  const activeBox = session.boxes[activeBoxIdx] ?? null;
  const lineStates = session.lineStates;

  const unpackedCount = order.lines.filter(
    l =>
      !lineStates[l.shopifyLineItemGid]?.boxId && !lineStates[l.shopifyLineItemGid]?.exceptionType,
  ).length;

  const checkAllDone = (states: PackSession['lineStates']) =>
    order.lines.every(
      l => states[l.shopifyLineItemGid]?.boxId || states[l.shopifyLineItemGid]?.exceptionType,
    );

  const handleAddBox = () => {
    const sizeInfo = BOX_SIZES.find(s => s.size === newBoxSize) ?? BOX_SIZES[0];
    const newBox = { id: nextBoxId(), size: sizeInfo.size, tareLbs: sizeInfo.tareLbs };
    const updatedSession = { ...session, boxes: [...session.boxes, newBox] };
    onSessionChange(updatedSession);
    setActiveBoxIdx(updatedSession.boxes.length - 1);
    setFocusTrigger(t => t + 1);
  };

  const handleRemoveBox = (boxId: string) => {
    const newBoxes = session.boxes.filter(b => b.id !== boxId);
    const newLineStates = Object.fromEntries(
      Object.entries(session.lineStates).map(([gid, ls]) => [
        gid,
        ls.boxId === boxId ? { ...ls, boxId: null } : ls,
      ]),
    );
    onSessionChange({ ...session, boxes: newBoxes, lineStates: newLineStates });
    setActiveBoxIdx(idx => Math.min(idx, Math.max(newBoxes.length - 1, 0)));
  };

  const handleSizeChange = (boxId: string, size: BoxSize) => {
    const sizeInfo = BOX_SIZES.find(s => s.size === size);
    if (!sizeInfo) return;
    onSessionChange({
      ...session,
      boxes: session.boxes.map(b =>
        b.id === boxId ? { ...b, size: sizeInfo.size, tareLbs: sizeInfo.tareLbs } : b,
      ),
    });
  };

  const handleScan = (value: string) => {
    setScanError(null);
    setScanWarning(null);
    if (!activeBox) return;

    const result = matchScan(order, session, value);

    if (result.status === 'not_found') {
      setScanError(`"${value}" not found in this order. Check SKU or UPC and try again.`);
      setFocusTrigger(t => t + 1);
      return;
    }
    if (result.status === 'exception') {
      const label =
        EXCEPTION_TYPES.find(e => e.value === result.exceptionType)?.label ?? result.exceptionType;
      setScanWarning(`Item has an open exception: ${label}. Cannot pack.`);
      setFocusTrigger(t => t + 1);
      return;
    }
    if (result.status === 'already_packed') {
      const boxNum = session.boxes.findIndex(b => b.id === result.boxId) + 1;
      setScanWarning(`Already packed into Box ${boxNum}.`);
      setFocusTrigger(t => t + 1);
      return;
    }

    const newLineStates = {
      ...lineStates,
      [result.gid]: { ...lineStates[result.gid], boxId: activeBox.id },
    };
    const updated = { ...session, lineStates: newLineStates };
    onSessionChange(updated);
    setFocusTrigger(t => t + 1);

    if (checkAllDone(newLineStates)) onNext();
  };

  const handleOpenException = (gid: string) => {
    setExceptionGid(gid);
    setSelectedExcType(null);
    setExcNotes('');
  };

  const handleSubmitException = () => {
    if (!exceptionGid || !selectedExcType) return;
    const newLineStates = {
      ...lineStates,
      [exceptionGid]: {
        ...lineStates[exceptionGid],
        exceptionType: selectedExcType,
        exceptionReason: excNotes || null,
      },
    };
    onSessionChange({ ...session, lineStates: newLineStates });
    setExceptionGid(null);
    setFocusTrigger(t => t + 1);
    if (checkAllDone(newLineStates)) onNext();
  };

  // ── No boxes yet: first-box picker ────────────────────────────────────────
  if (session.boxes.length === 0) {
    const isBike = order.lines.some(l => l.itemType === 'bike' || l.itemType === 'frame');
    const allSizes = isBike ? BOX_SIZES : BOX_SIZES.filter(s => !s.size.startsWith('Bike'));
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Add First Box
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'grey.600', mb: 3 }}>
          Select the box size for your first box, then start scanning.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select value={newBoxSize} onChange={e => setNewBoxSize(e.target.value as BoxSize)}>
              {allSizes.map(s => (
                <MenuItem key={s.size} value={s.size}>
                  {s.size}&nbsp;
                  <Typography component="span" sx={{ fontSize: 11, color: 'grey.500' }}>
                    {s.dims} in · {s.tareLbs} lbs
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleAddBox}>
            Start Packing
          </Button>
        </Box>
        <Typography sx={{ fontSize: 12, color: 'info.main' }}>
          Suggested: <strong>{suggestFirstBox(order)}</strong>
        </Typography>
      </Box>
    );
  }

  // ── Boxes exist: scan interface ────────────────────────────────────────────
  const isBike = order.lines.some(l => l.itemType === 'bike' || l.itemType === 'frame');
  const allSizes = isBike ? BOX_SIZES : BOX_SIZES.filter(s => !s.size.startsWith('Bike'));

  return (
    <Box>
      {/* Box tabs + add-box control */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Tabs
          value={activeBoxIdx}
          onChange={(_, v) => {
            setActiveBoxIdx(v);
            setScanError(null);
            setScanWarning(null);
            setFocusTrigger(t => t + 1);
          }}
          sx={{ flex: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          {session.boxes.map((box, idx) => {
            const count = order.lines.filter(
              l => lineStates[l.shopifyLineItemGid]?.boxId === box.id,
            ).length;
            return (
              <Tab
                key={box.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    Box {idx + 1}
                    {count > 0 && (
                      <Chip
                        label={count}
                        size="small"
                        color="success"
                        sx={{ height: 18, fontSize: 11 }}
                      />
                    )}
                  </Box>
                }
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Active box: size selector + remove */}
      {activeBox && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
            p: 1.5,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 13, minWidth: 44 }}>
            Box {activeBoxIdx + 1}
          </Typography>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={activeBox.size}
              onChange={e => handleSizeChange(activeBox.id, e.target.value as BoxSize)}
            >
              {allSizes.map(s => (
                <MenuItem key={s.size} value={s.size}>
                  {s.size}&nbsp;
                  <Typography component="span" sx={{ fontSize: 11, color: 'grey.500' }}>
                    {s.dims} in · {s.tareLbs} lbs
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {session.boxes.length > 1 && (
            <Tooltip title="Remove box">
              <IconButton size="small" color="error" onClick={() => handleRemoveBox(activeBox.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Scan input */}
      <Box sx={{ mb: 3 }}>
        {scanWarning && (
          <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setScanWarning(null)}>
            {scanWarning}
          </Alert>
        )}
        <ScanInput
          key={`scan-${activeBoxIdx}`}
          value={scanValue}
          onChange={setScanValue}
          onScan={handleScan}
          error={scanError}
          label={`Scanning into Box ${activeBoxIdx + 1} — ${activeBox?.size ?? ''}`}
          focusTrigger={focusTrigger}
        />
      </Box>

      {/* Items list */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">
          Order Items
          {unpackedCount > 0 && (
            <Typography component="span" sx={{ fontSize: 12, color: 'grey.500', ml: 1 }}>
              {unpackedCount} remaining
            </Typography>
          )}
        </Typography>
      </Box>

      {order.lines.map(line => {
        const ls = lineStates[line.shopifyLineItemGid];
        const boxIdx = ls?.boxId ? session.boxes.findIndex(b => b.id === ls.boxId) : -1;
        const excLabel = ls?.exceptionType
          ? EXCEPTION_TYPES.find(e => e.value === ls.exceptionType)?.label
          : null;

        const badge = excLabel ? (
          <Chip label={excLabel} size="small" color="error" variant="outlined" />
        ) : ls?.boxId ? (
          <Chip label={`Box ${boxIdx + 1}`} size="small" color="success" />
        ) : (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={() => handleOpenException(line.shopifyLineItemGid)}
            sx={{ fontSize: 11, py: 0.25, whiteSpace: 'nowrap' }}
          >
            Flag Exception
          </Button>
        );

        return (
          <Box
            key={line.shopifyLineItemGid}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              py: 1.25,
              px: 1.5,
              mb: 0.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: excLabel ? 'error.light' : ls?.boxId ? 'success.light' : 'grey.200',
              bgcolor: excLabel ? '#fff5f5' : ls?.boxId ? '#f0faf4' : 'white',
            }}
          >
            <PackLineItem
              line={line}
              actions={badge}
              note={
                ls?.exceptionReason ? (
                  <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.25 }}>
                    {ls.exceptionReason}
                  </Typography>
                ) : (
                  undefined
                )
              }
            />
          </Box>
        );
      })}

      {/* Add another box */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mt: 2,
          p: 1.5,
          border: '1px dashed',
          borderColor: 'grey.300',
          borderRadius: 1,
        }}
      >
        <Typography sx={{ fontSize: 13, color: 'grey.600', flexShrink: 0 }}>
          Need another box?
        </Typography>
        <FormControl size="small" sx={{ flex: 1 }}>
          <Select value={newBoxSize} onChange={e => setNewBoxSize(e.target.value as BoxSize)}>
            {allSizes.map(s => (
              <MenuItem key={s.size} value={s.size}>
                {s.size}&nbsp;
                <Typography component="span" sx={{ fontSize: 11, color: 'grey.500' }}>
                  {s.dims} in · {s.tareLbs} lbs
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddBox}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          Add Box {session.boxes.length + 1}
        </Button>
      </Box>

      <Button
        variant="contained"
        size="large"
        disabled={unpackedCount > 0}
        onClick={onNext}
        sx={{ mt: 3 }}
      >
        Continue to Packing Slips
      </Button>

      {/* Exception dialog */}
      <Dialog fullScreen open={!!exceptionGid} onClose={() => setExceptionGid(null)}>
        <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
          <Toolbar>
            <IconButton edge="start" onClick={() => setExceptionGid(null)} aria-label="Close">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
              Flag Exception
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3 }}>
          {exceptionGid && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {order.lines.find(l => l.shopifyLineItemGid === exceptionGid)?.sku}
            </Typography>
          )}
          <Typography variant="subtitle2" mb={1}>
            Reason
          </Typography>
          {EXCEPTION_TYPES.map(t => (
            <Button
              key={t.value}
              variant={selectedExcType === t.value ? 'contained' : 'outlined'}
              color={selectedExcType === t.value ? 'error' : 'inherit'}
              fullWidth
              sx={{ mb: 1, justifyContent: 'flex-start', textTransform: 'none' }}
              onClick={() => setSelectedExcType(t.value)}
            >
              {t.label}
            </Button>
          ))}
          <TextField
            label="Notes (optional)"
            multiline
            rows={3}
            fullWidth
            value={excNotes}
            onChange={e => setExcNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Button
            variant="contained"
            color="error"
            fullWidth
            disabled={!selectedExcType}
            onClick={handleSubmitException}
            sx={{ mt: 3 }}
          >
            Submit Exception
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default MockPackScanStep;
